import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/LoadingSpinner';
import { MapPin, Check, AlertTriangle, ChevronRight } from 'lucide-react';

/**
 * Pre-Bid Confirmation Modal
 * Shows user's primary shipping address before they can place bids.
 * Auto-dismisses after 2 seconds if address is confirmed.
 */
const PreBidConfirmation = ({ 
  auctionId, 
  onConfirm, 
  onCancel,
  autoConfirmDelay = 2000 // 2 seconds
}) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [addressData, setAddressData] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  
  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const res = await api().get(`/addresses/confirm-for-auction/${auctionId}`);
        setAddressData(res.data);
        
        if (res.data.has_address) {
          // Start countdown for auto-confirm
          setCountdown(autoConfirmDelay / 1000);
        }
      } catch (e) {
        setAddressData({
          has_address: false,
          message: 'Failed to load address'
        });
      }
      setLoading(false);
    };
    
    fetchAddress();
  }, [auctionId, autoConfirmDelay]);
  
  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    
    const timer = setTimeout(() => {
      setCountdown(c => c - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [countdown]);
  
  // Auto-confirm when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && addressData?.has_address && !confirmed) {
      handleConfirm();
    }
  }, [countdown, addressData, confirmed]);
  
  const handleConfirm = () => {
    setConfirmed(true);
    onConfirm(addressData?.address);
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }
  
  // No address - show warning
  if (!addressData?.has_address) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Shipping Address Required
            </h3>
            <p className="text-zinc-400">
              You need to add a shipping address before you can bid on auctions.
            </p>
          </div>
          
          <div className="space-y-3">
            <Link to="/settings?tab=addresses" className="block">
              <Button className="w-full bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black" data-testid="add-shipping-address-btn">
                <MapPin className="w-4 h-4 mr-2" />
                Add Shipping Address
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full text-zinc-400"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Has address - show confirmation
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full">
        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${
            confirmed ? 'bg-emerald-500' : 'bg-[#00BFFF]/20'
          }`}>
            {confirmed ? (
              <Check className="w-8 h-8 text-white" />
            ) : (
              <MapPin className="w-8 h-8 text-[#00BFFF]" />
            )}
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {confirmed ? 'Ready to Bid!' : 'Confirm Shipping Address'}
          </h3>
          <p className="text-zinc-400">
            {confirmed 
              ? 'Your winning items will ship to this address.'
              : 'Winning cards will be shipped to:'}
          </p>
        </div>
        
        {/* Address Display */}
        <div className={`p-4 rounded-xl mb-6 transition-colors ${
          confirmed 
            ? 'bg-emerald-500/10 border border-emerald-500/30' 
            : 'bg-zinc-800 border border-zinc-700'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              confirmed ? 'bg-emerald-500/20' : 'bg-[#00BFFF]/20'
            }`}>
              <MapPin className={`w-5 h-5 ${confirmed ? 'text-emerald-400' : 'text-[#00BFFF]'}`} />
            </div>
            <div>
              <p className="font-semibold text-white">{addressData.address?.name}</p>
              <p className="text-zinc-300 text-sm">{addressData.address?.street1}</p>
              <p className="text-zinc-300 text-sm">
                {addressData.address?.city}, {addressData.address?.state} {addressData.address?.zip}
              </p>
            </div>
          </div>
        </div>
        
        {!confirmed ? (
          <>
            {/* Countdown Progress */}
            {countdown > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-zinc-400 mb-1">
                  <span>Auto-confirming in {countdown}s</span>
                </div>
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#00BFFF] transition-all duration-1000"
                    style={{ width: `${(countdown / (autoConfirmDelay / 1000)) * 100}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-3">
              <Button 
                className="w-full bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-semibold"
                onClick={handleConfirm}
              >
                <Check className="w-4 h-4 mr-2" />
                Confirm & Start Bidding
              </Button>
              
              <div className="flex gap-3">
                <Link to="/settings?tab=addresses" className="flex-1">
                  <Button variant="outline" className="w-full border-zinc-700 text-sm" data-testid="change-address-btn">
                    Change Address
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="flex-1 text-zinc-400 text-sm"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center">
            <p className="text-emerald-400 text-sm flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Address confirmed! Good luck bidding!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreBidConfirmation;
